import { apiClient as api } from './api';

const saasPlatformService = {
  getDashboardStats: async () => {
    const res = await api.get('/platform/dashboard');
    return res.data;
  },

  getTenants: async (filterOrSearch = '', status = '') => {
    let searchVal = '';
    let statusVal = '';
    if (typeof filterOrSearch === 'object' && filterOrSearch !== null) {
      searchVal = filterOrSearch.search || '';
      statusVal = filterOrSearch.status || '';
    } else {
      searchVal = filterOrSearch || '';
      statusVal = status || '';
    }
    const params = {};
    if (searchVal) params.search = searchVal;
    if (statusVal) params.status = statusVal;
    const res = await api.get('/platform/tenants', { params });
    return res.data;
  },

  getTenantDetails: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}`);
    return res.data;
  },

  onboardTenant: async (tenantData) => {
    const res = await api.post('/platform/tenants', tenantData);
    return res.data;
  },

  suspendTenant: async (tenantId, reason = '') => {
    const body = reason ? { reason } : {};
    const res = await api.post(`/platform/tenants/${tenantId}/suspend`, body);
    return res.data;
  },

  reactivateTenant: async (tenantId) => {
    const res = await api.post(`/platform/tenants/${tenantId}/reactivate`);
    return res.data;
  },

  getPlatformHealth: async () => {
    const res = await api.get('/platform/health');
    return res.data;
  },

  getAuditLogs: async (filterOrTenantId = null, limit = 50) => {
    let tenantIdVal = null;
    let limitVal = limit;
    let actionVal = null;
    if (typeof filterOrTenantId === 'object' && filterOrTenantId !== null) {
      tenantIdVal = filterOrTenantId.tenantId || null;
      limitVal = filterOrTenantId.limit || limit;
      actionVal = filterOrTenantId.action || null;
    } else {
      tenantIdVal = filterOrTenantId;
    }
    const params = { limit: limitVal };
    if (tenantIdVal) params.tenantId = tenantIdVal;
    if (actionVal) params.action = actionVal;
    const res = await api.get('/platform/audit', { params });
    return res.data;
  },

  getFleetHealth: async () => {
    const res = await api.get('/platform/health/fleet');
    return res.data;
  },

  checkTenantHealth: async (tenantId) => {
    const res = await api.post(`/platform/health/check?tenantId=${tenantId}`);
    return res.data;
  },

  getTenantHealthHistory: async (tenantId, limit = 50) => {
    const res = await api.get(`/platform/tenants/${tenantId}/health/history`, { params: { limit } });
    return res.data;
  },

  getIncidents: async (filters = {}) => {
    const res = await api.get('/platform/incidents', { params: filters });
    return res.data;
  },

  getIncidentDetails: async (incidentId) => {
    const res = await api.get(`/platform/incidents/${incidentId}`);
    return res.data;
  },

  acknowledgeIncident: async (incidentId, operatorName = 'PlatformAdmin', comment = '') => {
    const res = await api.post(`/platform/incidents/${incidentId}/acknowledge`, { operatorName, comment });
    return res.data;
  },

  resolveIncident: async (incidentId, comment, operatorName = 'PlatformAdmin') => {
    const res = await api.post(`/platform/incidents/${incidentId}/resolve`, { operatorName, comment });
    return res.data;
  },

  getDeadLetterDeliveries: async (limit = 50) => {
    const res = await api.get('/platform/incidents/dead-letter', { params: { limit } });
    return res.data;
  },

  retryDeadLetter: async (deliveryId) => {
    const res = await api.post(`/platform/incidents/dead-letter/${deliveryId}/retry`);
    return res.data;
  },

  remediateTenant: async (tenantId, action, reason = '') => {
    const res = await api.post(`/platform/tenants/${tenantId}/remediate`, { action, reason });
    return res.data;
  },

  // Cryptographic Key Lifecycle & Retirement Administration
  getKeyInventory: async () => {
    const res = await api.get('/platform/keys/inventory');
    return res.data;
  },

  dryRunKeyRetirement: async (version) => {
    const res = await api.post(`/platform/keys/${version}/dry-run`);
    return res.data;
  },

  deprecateKey: async (version, reason = '') => {
    const res = await api.post(`/platform/keys/${version}/deprecate`, { reason });
    return res.data;
  },

  requestKeyRetirement: async (version, data) => {
    const res = await api.post(`/platform/keys/${version}/request-retirement`, data);
    return res.data;
  },

  confirmKeyRetirement: async (version, data) => {
    const res = await api.post(`/platform/keys/${version}/confirm-retirement`, data);
    return res.data;
  },

  cancelKeyRetirement: async (version, reason = '') => {
    const res = await api.post(`/platform/keys/${version}/cancel-retirement`, { reason });
    return res.data;
  },

  submitRecoveryReview: async (version, data) => {
    const res = await api.post(`/platform/keys/${version}/recovery-review`, data);
    return res.data;
  },

  getKeyHistory: async (limit = 50) => {
    const res = await api.get('/platform/keys/history', { params: { limit } });
    return res.data;
  },

  // Tamper-Evident Audit & Integrity APIs
  getStreamIntegritySummaries: async () => {
    const res = await api.get('/platform/tenants/audit/integrity/streams');
    return res.data;
  },

  verifyStreamIntegrity: async (streamId, upToSequence = null) => {
    const res = await api.post('/platform/tenants/audit/integrity/verify', { streamId, upToSequence });
    return res.data;
  },

  verifySingleEvent: async (auditId) => {
    const res = await api.get(`/platform/tenants/audit/integrity/events/${auditId}/verify`);
    return res.data;
  },

  getIntegrityCheckpoints: async (streamId = null) => {
    const res = await api.get('/platform/tenants/audit/integrity/checkpoints', { params: { streamId } });
    return res.data;
  },

  createIntegrityCheckpoint: async (data) => {
    const res = await api.post('/platform/tenants/audit/integrity/checkpoints', data);
    return res.data;
  },

  getIntegrityIncidents: async (streamId = null, status = null) => {
    const res = await api.get('/platform/tenants/audit/integrity/incidents', { params: { streamId, status } });
    return res.data;
  },

  acknowledgeIntegrityIncident: async (incidentId, operatorComment) => {
    const res = await api.post(`/platform/tenants/audit/integrity/incidents/${incidentId}/acknowledge`, { operatorComment });
    return res.data;
  },

  verifyBillingSnapshot: async (snapshotRecord) => {
    const res = await api.post('/platform/tenants/audit/integrity/billing-snapshot/verify', snapshotRecord);
    return res.data;
  },

  getIntegrityReport: async (streamId) => {
    const res = await api.get('/platform/tenants/audit/integrity/report', { params: { streamId } });
    return res.data;
  },

  // Fleet Compatibility, Schema Drift & Disposable Dry-Run
  getFleetCompatibility: async () => {
    const res = await api.get('/platform/fleet/compatibility');
    return res.data;
  },

  getTenantCompatibility: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/compatibility`);
    return res.data;
  },

  executeMigrationDryRun: async (payload) => {
    const res = await api.post('/platform/fleet/dry-run', payload);
    return res.data;
  },

  // Secret Rotation
  rotateTenantSecret: async (tenantId, targetKeyVersion = 'v1') => {
    const res = await api.post(`/platform/tenants/${tenantId}/secrets/rotate`, { targetKeyVersion });
    return res.data;
  },

  rotateFleetSecrets: async (targetKeyVersion = 'v1') => {
    const res = await api.post('/platform/fleet/secrets/rotate', { targetKeyVersion });
    return res.data;
  },

  // Tenant Offboarding (Retention Hold)
  offboardTenant: async (tenantId, reason, retentionDays = 90) => {
    const res = await api.post(`/platform/tenants/${tenantId}/offboard`, { reason, retentionDays });
    return res.data;
  },

  // Enterprise Control Center Expansion APIs
  getOverviewMetrics: async () => {
    const res = await api.get('/platform/overview/metrics');
    return res.data;
  },

  getPlans: async () => {
    const res = await api.get('/platform/plans');
    return res.data;
  },

  getTenantSubscription: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/subscription`);
    return res.data;
  },

  updateTenantSubscription: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/subscription`, data);
    return res.data;
  },

  getTenantWallet: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/wallet`);
    return res.data;
  },

  getTenantWalletLedger: async (tenantId, limit = 50) => {
    const res = await api.get(`/platform/tenants/${tenantId}/wallet/ledger`, { params: { limit } });
    return res.data;
  },

  topupTenantWallet: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/wallet/topup`, data);
    return res.data;
  },

  getTenantUsage: async (tenantId, period = '') => {
    const res = await api.get(`/platform/tenants/${tenantId}/usage`, { params: { period } });
    return res.data;
  },

  getTenantInvoices: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/invoices`);
    return res.data;
  },

  recordInvoicePayment: async (tenantId, invoiceId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/invoices/${invoiceId}/record-payment`, data);
    return res.data;
  },

  getTenantLifecycleStatus: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/lifecycle-status`);
    return res.data;
  },

  lockTenant: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/lock`, data);
    return res.data;
  },

  unlockTenant: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/unlock`, data);
    return res.data;
  },

  updateTenantModules: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/modules`, data);
    return res.data;
  },

  updateTenantGovernanceQuotas: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/governance-quotas`, data);
    return res.data;
  },

  getTenantInvestigations: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/clinical/investigations`);
    return res.data;
  },

  getInvestigationRanges: async (tenantId, investigationId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/clinical/investigations/${investigationId}/ranges`);
    return res.data;
  },

  createRangeDraft: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/clinical/ranges/draft`, data);
    return res.data;
  },

  approveRangeDraft: async (tenantId, versionId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/clinical/ranges/${versionId}/approve`, data);
    return res.data;
  },

  getTenantCentres: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/centres`);
    return res.data;
  },

  createTenantCentre: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/centres`, data);
    return res.data;
  },

  getTenantStaff: async (tenantId) => {
    const res = await api.get(`/platform/tenants/${tenantId}/staff`);
    return res.data;
  },

  createTenantStaff: async (tenantId, data) => {
    const res = await api.post(`/platform/tenants/${tenantId}/staff`, data);
    return res.data;
  },

  // Commercial Licensing & Module Control
  getLicensePlans: async () => {
    const res = await api.get('/platform/licensing/plans');
    return res.data;
  },

  getTenantLicenseProfile: async (tenantId) => {
    const res = await api.get(`/platform/licensing/tenants/${tenantId}`);
    return res.data;
  },

  applyLicensePlan: async (tenantId, planCode) => {
    const res = await api.post(`/platform/licensing/tenants/${tenantId}/apply-plan`, { planCode });
    return res.data;
  },

  updateTenantScreenEntitlement: async (tenantId, screenCode, isLicensed, allowedActions = '*') => {
    const res = await api.post(`/platform/licensing/tenants/${tenantId}/screens`, {
      screenCode,
      isLicensed,
      allowedActions
    });
    return res.data;
  },

  updateTenantElementOverride: async (tenantId, elementKey, isEnabled) => {
    const res = await api.post(`/platform/licensing/tenants/${tenantId}/elements`, {
      elementKey,
      isEnabled
    });
    return res.data;
  },

  getTenantFeatureElements: async (tenantId, screenCode = null) => {
    const res = await api.get(`/platform/licensing/tenants/${tenantId}/elements`, {
      params: screenCode ? { screenCode } : {}
    });
    return res.data;
  },

  evictTenantLicenseCache: async (tenantId) => {
    const res = await api.post(`/platform/licensing/tenants/${tenantId}/cache-evict`);
    return res.data;
  }
};

export default saasPlatformService;
